// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessBilling.jsx
// Название: Business Billing - Subscription Only
// Версия: 5.0.0
// Дата: 27.01.2026

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

const BusinessBilling = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subRes, walletRes] = await Promise.all([
        api.get('/subscriptions').catch(() => null),
        api.get('/brand-wallet').catch(() => null)
      ]);
      
      if (subRes) {
        const sub = subRes.subscription || subRes.data?.subscription;
        if (sub) {
          setSubscription({
            plan: sub.plan_code,
            status: sub.status,
            expires_at: sub.expires_at,
            auto_renew: sub.auto_renew
          });
        }
      }
      
      if (walletRes) {
        setWallet(walletRes.wallet || walletRes.data?.wallet);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planCode) => {
    if (processing) return;
    try {
      setProcessing(true);
      setMessage({ type: '', text: '' });
      
      const res = await api.post('/subscriptions/checkout', { plan_code: planCode });
      
      if (res.provider === 'mock') {
        await api.post('/subscriptions/activate', { plan_code: planCode });
        await loadData();
        showMessage('success', '✅ Подписка успешно активирована!');
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err) {
      showMessage('error', err.message || 'Ошибка оформления подписки');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Отключить автопродление подписки?')) return;
    try {
      setProcessing(true);
      await api.post('/subscriptions/cancel');
      await loadData();
      showMessage('success', 'Автопродление отключено');
    } catch (err) {
      showMessage('error', err.message || 'Ошибка');
    } finally {
      setProcessing(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    if (type === 'success') setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return 0;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const formatAmount = (amount) => {
    return parseFloat(amount || 0).toLocaleString('ru-RU', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  };

  // Computed
  const daysLeft = subscription ? getDaysUntil(subscription.expires_at) : 0;
  const isExpiring = daysLeft <= 7 && daysLeft > 0;
  const isExpired = daysLeft <= 0;
  const isTrial = subscription?.plan === 'trial';
  const totalReserved = parseFloat(wallet?.promo_reserve || 0) + parseFloat(wallet?.fees_reserve || 0);

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loading}>⏳ Загрузка...</div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>💳 Подписка</h1>
          <p style={s.subtitle}>Управление подпиской DARY Business</p>
        </div>
        <button style={s.refreshBtn} onClick={loadData}>🔄 Обновить</button>
      </div>

      {/* Messages */}
      {message.text && (
        <div style={{...s.message, ...(message.type === 'success' ? s.msgSuccess : s.msgError)}}>
          {message.text}
        </div>
      )}

      <div style={s.grid}>
        {/* Subscription Card */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div>
              <span style={{
                ...s.badge,
                ...(isExpired ? s.badgeRed : isTrial ? s.badgePurple : isExpiring ? s.badgeYellow : s.badgeGreen)
              }}>
                {isExpired ? '❌ Истекла' : isTrial ? '🎁 Триал' : isExpiring ? '⚠️ Заканчивается' : '✅ Активна'}
              </span>
            </div>
            <div style={s.price}>
              <span style={{...s.priceValue, color: isTrial ? '#8B5CF6' : '#10B981'}}>
                €{isTrial ? '0' : '249'}
              </span>
              <span style={s.pricePeriod}>/мес</span>
            </div>
          </div>

          <div style={s.infoList}>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Тарифный план</span>
              <span style={s.infoValue}>{isTrial ? 'Триал (14 дней)' : 'Месячный'}</span>
            </div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Действует до</span>
              <span style={{...s.infoValue, color: isExpiring ? '#d97706' : isExpired ? '#dc2626' : '#1f2937'}}>
                {formatDate(subscription?.expires_at)}
              </span>
            </div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Осталось дней</span>
              <span style={{...s.infoValue, color: daysLeft <= 7 ? '#dc2626' : '#10B981'}}>
                {daysLeft > 0 ? `${daysLeft} дней` : 'Истекла'}
              </span>
            </div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Автопродление</span>
              <span style={s.infoValue}>{subscription?.auto_renew ? '✓ Включено' : '✗ Выключено'}</span>
            </div>
            <div style={s.infoRow}>
              <span style={s.infoLabel}>Лимит кампаний</span>
              <span style={s.infoValue}>100</span>
            </div>
          </div>

          {/* Warnings */}
          {isTrial && (
            <div style={{...s.warning, ...s.warningPurple}}>
              🎁 Триал период. Активируйте подписку для полного доступа.
            </div>
          )}
          {isExpiring && !isTrial && (
            <div style={{...s.warning, ...s.warningYellow}}>
              ⚠️ Подписка заканчивается через {daysLeft} дней. Продлите сейчас!
            </div>
          )}
          {isExpired && (
            <div style={{...s.warning, ...s.warningRed}}>
              ❌ Подписка истекла. Активируйте для доступа к функциям.
            </div>
          )}

          {/* Actions */}
          <div style={s.actions}>
            <button 
              style={{...s.btnGreen, ...(processing && s.btnDisabled)}}
              onClick={() => handleSubscribe('monthly')}
              disabled={processing}
            >
              {processing ? '⏳...' : (isTrial || isExpired ? 'Активировать €249' : 'Продлить €249')}
            </button>
            <button 
              style={{...s.btnBlue, ...(processing && s.btnDisabled)}}
              onClick={() => handleSubscribe('yearly')}
              disabled={processing}
            >
              Годовой €2490 (-17%)
            </button>
          </div>

          {!isTrial && subscription?.auto_renew && !isExpired && (
            <button style={s.cancelLink} onClick={handleCancelSubscription}>
              Отключить автопродление
            </button>
          )}
        </div>

        {/* Wallet Summary Card */}
        <div style={s.card}>
          <h2 style={s.cardTitle}>💰 Brand Wallet</h2>
          
          <div style={s.walletStats}>
            <div style={{...s.statBox, background: '#f0fdf4', borderColor: '#86efac'}}>
              <div style={{...s.statValue, color: '#10B981'}}>€{formatAmount(wallet?.available_balance)}</div>
              <div style={{...s.statLabel, color: '#166534'}}>Доступно</div>
            </div>
            <div style={{...s.statBox, background: '#fef3c7', borderColor: '#fcd34d'}}>
              <div style={{...s.statValue, color: '#d97706'}}>€{formatAmount(totalReserved)}</div>
              <div style={{...s.statLabel, color: '#92400e'}}>В кампаниях</div>
            </div>
          </div>

          <div style={s.walletInfo}>
            <p style={s.walletText}>
              <strong>€20 за лида</strong> — фиксированная стоимость активированного клиента
            </p>
            <p style={s.walletText}>
              Средства резервируются при создании кампании и списываются по мере активации лидов
            </p>
          </div>

          <button 
            style={s.walletBtn}
            onClick={() => navigate('/business/wallet')}
          >
            💰 Перейти в кошелёк
          </button>
        </div>
      </div>
    </div>
  );
};

// Styles
const s = {
  page: { padding: 24, maxWidth: 1000, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  loading: { textAlign: 'center', padding: 60, fontSize: 18, color: '#6b7280' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, color: '#1f2937', marginBottom: 4 },
  subtitle: { fontSize: 15, color: '#6b7280' },
  refreshBtn: { padding: '10px 20px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 14 },
  
  message: { padding: '12px 16px', borderRadius: 10, marginBottom: 20, fontSize: 14, fontWeight: 500 },
  msgSuccess: { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
  msgError: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },
  
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  card: { background: 'white', borderRadius: 20, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6' },
  cardTitle: { fontSize: 18, fontWeight: 700, color: '#1f2937', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  
  badge: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 },
  badgeGreen: { background: '#dcfce7', color: '#166534' },
  badgeYellow: { background: '#fef3c7', color: '#92400e' },
  badgeRed: { background: '#fee2e2', color: '#991b1b' },
  badgePurple: { background: '#f3e8ff', color: '#7c3aed' },
  
  price: { textAlign: 'right' },
  priceValue: { fontSize: 32, fontWeight: 800 },
  pricePeriod: { fontSize: 14, color: '#6b7280' },
  
  infoList: { marginBottom: 20 },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f3f4f6' },
  infoLabel: { color: '#6b7280', fontSize: 14 },
  infoValue: { fontWeight: 600, fontSize: 14, color: '#1f2937' },
  
  warning: { marginBottom: 20, padding: '14px 16px', borderRadius: 12, fontSize: 14, fontWeight: 500 },
  warningPurple: { background: '#f3e8ff', color: '#7c3aed', border: '1px solid #c4b5fd' },
  warningYellow: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' },
  warningRed: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },
  
  actions: { display: 'flex', gap: 12, marginBottom: 12 },
  btnGreen: { flex: 1, padding: '14px 20px', background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 15 },
  btnBlue: { flex: 1, padding: '14px 20px', background: 'linear-gradient(135deg, #3B82F6, #2563eb)', color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 15 },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  cancelLink: { display: 'block', width: '100%', textAlign: 'center', background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', marginTop: 8 },
  
  walletStats: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 },
  statBox: { textAlign: 'center', padding: '20px 16px', borderRadius: 16, border: '2px solid' },
  statValue: { fontSize: 28, fontWeight: 800, marginBottom: 4 },
  statLabel: { fontSize: 12, fontWeight: 600, textTransform: 'uppercase' },
  
  walletInfo: { marginBottom: 20, padding: 16, background: '#f9fafb', borderRadius: 12 },
  walletText: { fontSize: 14, color: '#4b5563', marginBottom: 8, lineHeight: 1.5 },
  walletBtn: { width: '100%', padding: '14px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 15 }
};

export default BusinessBilling;